import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { useBranchesList, useTransferAsset } from "../hooks/useAssetRegistry";
import type { Asset } from "../types";
import { extractError } from "../utils";

interface Props {
  open: boolean;
  onClose: () => void;
  asset: Asset | null;
}

export function TransferAssetModal({ open, onClose, asset }: Props) {
  const [branch, setBranch] = useState("");
  const [error, setError] = useState("");
  const { data: branches = [], isLoading } = useBranchesList(open);
  const transfer = useTransferAsset();

  useEffect(() => {
    if (open) {
      setBranch(asset?.branch ?? "");
      setError("");
    }
  }, [open, asset]);

  function submit() {
    if (!asset) return;
    if (!branch) {
      setError("Wskaż oddział.");
      return;
    }
    transfer.mutate(
      { id: asset.id, branch },
      { onSuccess: () => onClose(), onError: (e) => setError(extractError(e)) },
    );
  }

  return (
    <Modal open={open} onClose={onClose} title={asset ? `Przenieś: ${asset.name}` : "Przenieś sprzęt"}>
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
            Oddział docelowy *
          </label>
          <select
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
            disabled={isLoading}
            className="w-full px-3 py-2 text-sm bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-primary)]"
          >
            <option value="">{isLoading ? "Ładowanie…" : "— wybierz —"}</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          {asset?.branch_name && (
            <p className="text-xs text-[var(--color-text-muted)] mt-1">
              Obecny oddział: {asset.branch_name}
            </p>
          )}
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={submit} loading={transfer.isPending}>
            Przenieś
          </Button>
        </div>
      </div>
    </Modal>
  );
}
